import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Album } from './albums'
import { api } from './api'
import { localDay } from './home'

// The Modern style of Photos (shared/sections.ts): the rings along the top, the stories they open, the photos related
// to one, and the wall's layout.

export type RingPerson = {
  id: string
  name: string
  living: boolean
  pet: boolean
  portrait: string | null
  photos: number
  // Someone else has added a photo of them since you last opened their ring (within 30 days).
  fresh: boolean
}
export type WallPhoto = {
  id: string
  ref: string
  width: number
  height: number
  caption: string
  takenOn: string | null
  // Its album, and whether you may send it to another; missing where the list doesn't say.
  album?: { id: string; title: string } | null
  canEdit?: boolean
}
export type Rings = {
  day: { photos: number; cover: Pick<WallPhoto, 'id' | 'ref' | 'width' | 'height'>; fresh: boolean } | null
  people: RingPerson[]
}
export type Ring = { kind: 'day' } | { kind: 'person'; person: RingPerson }
export type StoryPhoto = WallPhoto & { createdAt: number; by: string; fresh: boolean }
export type Related = { kind: 'day' | 'album' | null; title: string; photos: WallPhoto[] }

export const ringKey = (ring: Ring) => (ring.kind === 'day' ? 'day' : `person:${ring.person.id}`)

// The rings in order: On this day first, then the people as the server ranked them.
export const ringList = (rings: Rings): Ring[] => [
  ...(rings.day ? [{ kind: 'day' as const }] : []),
  ...rings.people.map((person) => ({ kind: 'person' as const, person })),
]

const query = (values: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(values)) if (v) q.set(k, v)
  return q.toString()
}

export const useRings = (familyId: string, spaceId?: string) =>
  useQuery({
    queryKey: ['rings', familyId, spaceId ?? 'all'],
    queryFn: () => api<Rings>(`/families/${familyId}/rings?${query({ space: spaceId, day: localDay() })}`),
  })

export const useStory = (familyId: string, ring: Ring | null, spaceId?: string) =>
  useQuery({
    queryKey: ['ring-story', familyId, ring && ringKey(ring), spaceId ?? 'all'],
    enabled: !!ring,
    queryFn: () =>
      api<{ photos: StoryPhoto[] }>(
        `/families/${familyId}/rings/story?${query({ space: spaceId, day: localDay(), ring: ring ? ringKey(ring) : '' })}`,
      ),
  })

export const relatedQuery = (photoId: string | null, familyId: string) => ({
  queryKey: ['related', photoId, familyId],
  queryFn: () => api<Related>(`/album-photos/${encodeURIComponent(photoId!)}/related?${query({ family: familyId })}`),
})

export const useRelated = (photoId: string | null, familyId: string) =>
  useQuery({ ...relatedQuery(photoId, familyId), enabled: !!photoId })

// Opening a ring: it stops showing as new at once, and the server remembers.
export function useSeeRing(familyId: string) {
  const queryClient = useQueryClient()
  return (ring: Ring) => {
    queryClient.setQueriesData<Rings>({ queryKey: ['rings', familyId] }, (rings) =>
      rings && ring.kind === 'day'
        ? { ...rings, day: rings.day && { ...rings.day, fresh: false } }
        : rings && {
            ...rings,
            people: rings.people.map((p) =>
              ring.kind === 'person' && p.id === ring.person.id ? { ...p, fresh: false } : p,
            ),
          },
    )
    void api(`/families/${familyId}/rings/seen`, { method: 'POST', body: { ring: ringKey(ring), day: localDay() } })
  }
}

// Where a story starts: at the first photo you haven't seen, or at the beginning when you've seen them all.
export function storyStart(photos: { fresh: boolean }[]) {
  const first = photos.findIndex((x) => x.fresh)
  return first < 0 ? 0 : first
}

// ---- The wall

// What the wall shows: every photo (newest added first), the albums, the people in the photos, or the photos with one
// tag: someone in them, the year they were taken, an album, or a word they're tagged with. Kept in the address as
// ?show=.
export type Show =
  | { kind: 'all' }
  | { kind: 'albums' }
  | { kind: 'people' }
  | { kind: 'person'; id: string }
  | { kind: 'year'; year: string }
  | { kind: 'album'; id: string }
  | { kind: 'tag'; tag: string }

export function showOf(value: string | null): Show {
  const [kind, ...rest] = (value ?? '').split(':')
  const arg = rest.join(':')
  if (kind === 'albums' || kind === 'people') return { kind }
  if (kind === 'person' && arg) return { kind, id: arg }
  if (kind === 'album' && arg) return { kind, id: arg }
  if (kind === 'year' && /^\d{4}$/.test(arg)) return { kind, year: arg }
  if (kind === 'tag' && arg) return { kind, tag: arg }
  return { kind: 'all' }
}

export const showValue = (show: Show) =>
  show.kind === 'all' || show.kind === 'albums' || show.kind === 'people'
    ? show.kind
    : `${show.kind}:${show.kind === 'year' ? show.year : show.kind === 'tag' ? show.tag : show.id}`

// The tab a view sits under: a year or a word narrows All, an album sits in Albums, a person in People.
export type PhotoTab = 'all' | 'albums' | 'people'
export const tabOf = (show: Show): PhotoTab =>
  show.kind === 'albums' || show.kind === 'album'
    ? 'albums'
    : show.kind === 'people' || show.kind === 'person'
      ? 'people'
      : 'all'

// Where a sideways swipe takes you among the tabs: swiping left (the finger moving left) goes to the next tab, right
// to the one before, as pages turn. It has to be clearly sideways, and either long enough to mean it or a quick flick
// that way (vx, in pixels a millisecond, as the finger let go); anything else is a scroll or a tap, and the page
// springs back. Nothing past either end.
export function swipeTab<T extends string>(tabs: T[], from: T, dx: number, dy: number, vx = 0): T | null {
  const flick = Math.abs(dx) >= 24 && Math.abs(vx) >= 0.35 && Math.sign(vx) === Math.sign(dx)
  if ((Math.abs(dx) < 60 && !flick) || Math.abs(dx) < Math.abs(dy) * 2) return null
  const at = tabs.indexOf(from)
  return tabs[at + (dx < 0 ? 1 : -1)] ?? null
}

// The photos for one tag: the same lists the rest of Photos uses, so the wall shows exactly what you may see.
export function wallQuery(familyId: string, show: Show, spaceId: string | undefined, mine: boolean) {
  return {
    queryKey: ['wall', familyId, showValue(show), spaceId ?? 'all', mine],
    queryFn: async (): Promise<WallPhoto[]> => {
      if (show.kind === 'album') return (await api<Album>(`/albums/${encodeURIComponent(show.id)}`)).photos
      const q = new URLSearchParams(
        show.kind === 'person'
          ? { person: show.id }
          : show.kind === 'year'
            ? { year: show.year }
            : show.kind === 'tag'
              ? { tag: show.tag }
              : {},
      )
      if (spaceId) q.set('space', spaceId)
      if (mine) q.set('mine', '1')
      return (await api<{ photos: WallPhoto[] }>(`/families/${familyId}/photos?${q}`)).photos
    },
  }
}

// A photo's picture on the wall, for a view transition to fly it from or to.
export const tileImage = (id: string) =>
  document.querySelector<HTMLImageElement>(`.mp-tile[data-photo="${CSS.escape(id)}"] .mp-img`)

// How many columns the wall has at a width (in CSS pixels): two on a phone, up to five on a big screen. Smaller
// photos (picked under Filter) add one more at every size, so three on a phone.
export const columnsFor = (width: number, smaller = false) =>
  (width < 640 ? 2 : width < 1024 ? 3 : width < 1440 ? 4 : 5) + (smaller ? 1 : 0)

// The space between photos on the wall: thin on a phone, like Pinterest, so the photos fill the screen.
export const gapFor = (width: number) => (width < 640 ? 4 : width < 1024 ? 12 : 16)

// How big photos are on the wall, picked under Filter and kept on this device.
export const SIZES = ['bigger', 'smaller'] as const
export type WallSize = (typeof SIZES)[number]
export const SIZE_KEY = 'gatheredfrom:photos-size'

// A photo keeps its own shape on the wall. Only the rare extreme one (a panorama, a phone's long screenshot) is held
// to a shape that still fits, cropping its tile but never the photo itself.
export const tileRatio = (photo: { width: number; height: number }) =>
  Math.min(Math.max(photo.width / Math.max(photo.height, 1), 0.45), 2.4)

export type Box = { x: number; y: number; w: number; h: number }

// The wall: each photo in turn goes to the shortest column, so the photos read left to right in the order they came
// and the columns end close to even. extra is room under a photo (for its caption). Positions are in pixels.
export function masonry(items: { ratio: number; extra: number }[], columns: number, width: number, gap: number) {
  const w = (width - gap * (columns - 1)) / columns
  const heights = Array.from({ length: columns }, () => 0)
  const boxes: Box[] = items.map(({ ratio, extra }) => {
    let col = 0
    for (let i = 1; i < columns; i++) if (heights[i]! < heights[col]! - 0.5) col = i
    const h = w / ratio
    const box = { x: col * (w + gap), y: heights[col]!, w, h }
    heights[col]! += h + extra + gap
    return box
  })
  return { boxes, height: Math.max(0, ...heights.map((x) => x - gap)) }
}

// The photos on screen (with a screen's margin above and below), so a wall of thousands draws only a few dozen.
export function visible(boxes: Box[], extra: number[], top: number, bottom: number) {
  const shown: number[] = []
  boxes.forEach((b, i) => {
    if (b.y + b.h + (extra[i] ?? 0) >= top && b.y <= bottom) shown.push(i)
  })
  return shown
}

// The photo after or before one in a list, for swiping through what's on the wall.
export function neighbour<T extends { id: string }>(list: T[], id: string, step: 1 | -1) {
  const at = list.findIndex((x) => x.id === id)
  return at < 0 ? null : (list[at + step] ?? null)
}

// When a photo was added, as a person says it: "today", "yesterday", "3 days ago", "2 weeks ago", then the date.
export function addedAgo(at: number, now = Date.now()) {
  const day = (t: number) => {
    const d = new Date(t)
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000
  }
  const days = day(now) - day(at)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 28) return `${Math.floor(days / 7)} ${days < 14 ? 'week' : 'weeks'} ago`
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}
